/**
 *
 * @author Juan Rico
 * @component NSTimer
 * @description  un boton con temporizador personalizable
 * normalmente usado para gestionar el tiempo global
 */

//#region  Importaciones necesarias para el componente NSTimer
import React, { useState, useEffect } from "react"; // Importa React y hooks.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; //importa componentes de fontawesome
import { faStopwatch } from "@fortawesome/free-solid-svg-icons"; //importa iconos de fontawesome
import { timerFormat, completeTimerFormat } from "../../../helpers/helpers"; //importar funciones de ayuda
import { isMobile } from "react-device-detect"; // importacion de elementos responsive
import useTranslation from "../../../hooks/useTranslation";
import {
  Chrono_Cards,
  Chrono_Cards_White,
  Ic_chrono_L,
} from "../../../helpers/ImagesCache";
import { PlatformMobile } from "../../../helpers/Constants";
//#endregion

const NSTimer = ({
  titleOn, //Titulo cuando su estado es activo
  titleOff, //Titulo cuando su estado esta inactivo - por defecto
  onCheckStart, //Accion callback para checkear el inicio,
  onCheckStop, //Accion callback para checkear la detencion,
  onStartTimer, //Accion callback al INICIAR el temporizador
  onStopTimer, //Accion callback al DETENER el temporizador
  milliseconds, //Activar la visualizacion de milisegundos en el temporizador
  forceDisable, //Forzar la desactivacion la accion de este componente
  forceStart, //Forzar INICIAR el temporizador
  forceStop, //Forzar DETENER el temporizador,
  customStyle,
}) => {
  const T = useTranslation();
  //#region ############## VARIABLES DE ESTADO GENERALES ##############/
  /**
   * Diferencia de tiempo
   * @type {Number}
   * @default null - mantiene el valor diferencia entre el timestamp inicial
   * y el timestamp actual para determinar el conteo relativo
   */
  const [diff, setDiff] = useState(null);

  /**
   * Tiempo incial
   * @type {Number}
   * @default null - mantiene el valor inicial timestamp
   * desde donde el cronometro comenzara a evaluar
   */
  const [initial, setInitial] = useState(null);

  /**
   * Estado actual del contador
   * @type {boolean}
   * @default false - controlar el cambio de estado del
   * componente cuando el contador ha iniciado
   */
  const [start, setStart] = useState(false);
  //#endregion

  //#region ############## FUNCIONES HANDLE BASICAS ##############//

  /**
   * Manejador del estado start para iniciarlo y tener un callback
   * @function handleToStart
   * Se utiliza para iniciar el contador y accionar el callback onStartTimer
   * en caso de que se pase la prop
   */
  const handleToStart = () => {
    setStart(true);
    setInitial(+new Date());
    if (onStartTimer) {
      onStartTimer({
        startAt: +new Date(),
        stopAt: null,
      });
    }
  };

  /**
   * Manejador del estado start para detenerlo y tener un callback
   * @function handleToStop
   * Se utiliza para detener el contador y accionar el callback onStopTimer
   * en caso de que se pase la prop
   */
  const handleToStop = () => {
    setStart(false);
    if (onStopTimer) {
      onStopTimer({
        startAt: initial,
        stopAt: initial + diff.getTime(),
      });
    }
    //alert('se detuvo con el valor initial=' + initial)
  };

  /**
   * Manejador del estado start para iniciarlo y detenerlo sin tener un callback
   * @function handleOnClick
   * Se utiliza para intercambiar el estado de la variable start
   * de tal manera que pueda iniciar o detener el contador sin
   * tener un callback asociado, solo las validaciones de forceStop y forceDisable
   * que determinan si estara o no activo el boton
   */
  const handleOnClick = () => {
    if (forceStop) return;
    if (forceDisable) return;
    if (start) {
      if (!onCheckStop) {
        handleToStop();
      } else {
        onCheckStop({
          startAt: +new Date(),
          stopAt: null,
        });
      }
    } else {
      if (!onCheckStart) {
        handleToStart();
      } else {
        onCheckStart({
          startAt: +new Date(),
          stopAt: null,
        });
      }
    }
  };

  //#endregion

  //#region ############## FUNCIONES AVANZADAS ##############//
  /**
   * Manejador del estado start para iniciarlo y detenerlo sin tener un callback
   * @function onForceStart
   * Se utiliza para intercambiar el estado de la variable start
   * de tal manera que pueda iniciar o detener el contador sin
   * tener un callback asociado, solo las validaciones de forceStop y forceDisable
   * que determinan si estara o no activo el boton
   */
  const onForceStart = () => {
    if (forceStart) {
      handleToStart();
    }
  };
  /**
   * Contador de milisegundos.
   * @function tick
   * Se encarga de contar y evaluar cada milisegundo
   * y setea la diferencia de tiempos entre el valor
   * inicial y el actual
   */
  const tick = () => {
    if (forceStop) return;
    const temporalDate = +new Date();
    setDiff(new Date(temporalDate - initial));
  };
  //#endregion

  //#region ############## USEEFFECTS ##############//
  // Hook que se ejecuta al montar el componente
  // registramos los ticks cada vez que inital o start cambian.
  useEffect(() => {
    if (forceStop) return;
    if (initial && start) {
      requestAnimationFrame(tick);
    }
  }, [initial, start]);

  // Hook que se ejecuta al montar el componente
  // registramos los ticks cada vez que diff o start cambian.
  useEffect(() => {
    if (forceStop) return;
    if (diff && start) {
      requestAnimationFrame(tick);
    }
  }, [diff, start]);

  // Hook que se ejecuta al montar el componente
  // registramos los ticks cada vez que diff o start cambian.
  useEffect(() => {
    if (onCheckStart && !start && forceStart) {
      onForceStart();
    }
  }, [forceStart]);

  // Hook que se ejecuta al montar el componente
  // registramos los ticks cada vez que diff o start cambian.
  useEffect(() => {
    if (onCheckStop && start && forceStop && !forceStart) {
      handleToStop();
    }
  }, [forceStop, start, forceStart]);

  //#endregion

  return (
    <>
      <div
        style={{
          width: PlatformMobile ? "180px" : "254px",
          height: "56px",
          opacity: forceDisable ? "50%" : "100%",
          cursor: forceDisable ? undefined : "pointer",
        }}
        className="py-[8px] pl-[16px] pr-[6px] flex justify-between shadow shadow-[#33333377] bg-white rounded items-center "
        onClick={handleOnClick}
      >
        <div className="flex  items-center text-sm  font-[700] text-[18px]">
          {start
            ? titleOn
              ? T(titleOn)
              : T("titulo")
            : titleOff
            ? T(titleOff)
            : T("titulo")}
        </div>
        <div
          style={{
            color: customStyle ? "black" : start ? "white" : "black",
            background: customStyle ? "white" : start ? "#0BAC9D" : "#00ffaa22",
            width: PlatformMobile ? "70%" : "50%",
          }}
          className="p-[8px] rounded-[6px] flex items-center rounded gap-2 justify-between "
        >
          <img
            src={start ? Chrono_Cards_White : Chrono_Cards}
            alt=""
            className="object-cover"
            style={{
              background: customStyle ? (start ? "#0BAC9D" : "#00ffaa22") : "",
              borderRadius: "4px",
            }}
          />
          <span className="flex items-center w-[80px] text-[20px] font-[400]">
            {timerFormat(diff)}
          </span>
        </div>
      </div>
    </>
  );
};

export default NSTimer;
